import { useEffect, useMemo, useState } from 'react';

import { useCreditMutations } from '../../../hooks/useApi';
import type {
    ApplyCompanyHistoricalLedgerImportResponse,
    CompanyHistoricalLedgerProspectiveSow,
    CompanyHistoricalLedgerImportPreviewResponse,
    PricingConfigSummary,
} from '../../../services/api';
import { Button } from '../../ui/button';
import { Select } from '../../ui/select';
import { cn } from '../../../lib/utils';
import {
    formFieldGroupNestedCardClassName,
    formFieldGroupSurfaceClassName,
} from '../../ui/formPrimitives';
import { Actions, DialogFrame } from './creditDialogPrimitives';

interface CompanyHistoricalLedgerImportDialogProps {
    open: boolean;
    onClose: () => void;
    companyId: number;
    companyName: string;
    pricingConfigs: PricingConfigSummary[];
    onApplied?: (result: ApplyCompanyHistoricalLedgerImportResponse) => void;
}

function formatDateTime(value: string): string {
    return new Date(value).toLocaleString();
}

function formatMatchReason(value: string, source: 'pro' | 'pulse'): string {
    if (value === 'current_user_membership') {
        return source === 'pro' ? 'user owns the Wevo' : 'user created the Pulse';
    }
    return value.replace(/_/g, ' ');
}

function formatSkipReason(value: string): string {
    return value.replace(/_/g, ' ');
}

function formatPoolType(value: string): string {
    return value.replace(/_/g, ' ');
}

function formatAmount(value: string | number): string {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed.toFixed(2) : String(value);
}

function formatPoolAmountsByType(amounts: Partial<Record<string, string>>): string {
    return Object.entries(amounts)
        .filter((entry): entry is [string, string] => entry[1] !== undefined)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([poolType, amount]) => `${formatPoolType(poolType)} (${formatAmount(amount)})`)
        .join(', ');
}

function formatSourceLabel(source: 'pro' | 'pulse'): string {
    return source === 'pro' ? 'Pro' : 'Pulse';
}

function toDateInputValue(value: string | null): string {
    return value ? value.slice(0, 10) : '';
}

function deriveSelectedDateRange(
    prospectiveSow: CompanyHistoricalLedgerProspectiveSow | null,
    selectedSources: Array<'pro' | 'pulse'>,
): { startDate: string; endDate: string } {
    if (!prospectiveSow) {
        return { startDate: '', endDate: '' };
    }

    const selectedOptions = prospectiveSow.sourceOptions.filter((option) =>
        selectedSources.includes(option.source),
    );
    if (selectedOptions.length === 0) {
        return { startDate: '', endDate: '' };
    }

    const startDate = [...selectedOptions]
        .map((option) => option.startDate)
        .sort((left, right) => left.localeCompare(right))[0];
    const endDates = selectedOptions.map((option) => option.endDate);
    const endDate = endDates.some((value) => value === null)
        ? ''
        : ([...endDates]
              .filter((value): value is string => value !== null)
              .sort((left, right) => right.localeCompare(left))[0] ?? '');

    return {
        startDate: startDate ? toDateInputValue(startDate) : '',
        endDate: toDateInputValue(endDate),
    };
}

function buildEditablePools(
    prospectiveSow: CompanyHistoricalLedgerProspectiveSow | null,
): Record<string, string> {
    if (!prospectiveSow) {
        return {};
    }

    return prospectiveSow.pools.reduce<Record<string, string>>((accumulator, pool) => {
        accumulator[pool.poolType] = formatAmount(pool.initialAmount);
        return accumulator;
    }, {});
}

function SummaryCard({ label, value }: { label: string; value: number }) {
    return (
        <div className="rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-3">
            <p className="text-xs font-medium uppercase tracking-wide text-neutral-500">{label}</p>
            <p className="mt-1 text-2xl font-semibold text-neutral-950">{value}</p>
        </div>
    );
}

export function CompanyHistoricalLedgerImportDialog({
    open,
    onClose,
    companyId,
    companyName,
    pricingConfigs,
    onApplied,
}: CompanyHistoricalLedgerImportDialogProps) {
    const { previewCompanyHistoricalSowImport, applyCompanyHistoricalSowImport } =
        useCreditMutations();
    const [preview, setPreview] = useState<CompanyHistoricalLedgerImportPreviewResponse | null>(
        null,
    );
    const [pricingConfigId, setPricingConfigId] = useState('');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [allowMissingActiveLegacyContract, setAllowMissingActiveLegacyContract] = useState(false);
    const [editablePools, setEditablePools] = useState<Record<string, string>>({});
    const [editableName, setEditableName] = useState('');
    const [selectedSources, setSelectedSources] = useState<Array<'pro' | 'pulse'>>([]);
    const [editableStartDate, setEditableStartDate] = useState('');
    const [editableEndDate, setEditableEndDate] = useState('');

    useEffect(() => {
        if (!open) {
            setPreview(null);
            setPricingConfigId('');
            setErrorMessage(null);
            setAllowMissingActiveLegacyContract(false);
            setEditablePools({});
            setEditableName('');
            setSelectedSources([]);
            setEditableStartDate('');
            setEditableEndDate('');
        }
    }, [open]);

    const creditPricingConfigs = useMemo(
        () => pricingConfigs.filter((config) => config.config.unitType === 'credit'),
        [pricingConfigs],
    );

    const handlePreview = async () => {
        setErrorMessage(null);
        try {
            const result = await previewCompanyHistoricalSowImport.mutateAsync(companyId);
            setPreview(result);
            setAllowMissingActiveLegacyContract(false);
            setEditablePools(buildEditablePools(result.prospectiveSow));
            setEditableName(result.prospectiveSow?.name ?? '');
            const nextSelectedSources = result.prospectiveSow?.defaultSelectedSources ?? [];
            setSelectedSources(nextSelectedSources);
            const nextDateRange = deriveSelectedDateRange(
                result.prospectiveSow,
                nextSelectedSources,
            );
            setEditableStartDate(nextDateRange.startDate);
            setEditableEndDate(nextDateRange.endDate);
            if (!creditPricingConfigs.some((config) => config.id === pricingConfigId)) {
                setPricingConfigId('');
            }
        } catch (error) {
            setErrorMessage(
                error instanceof Error
                    ? error.message
                    : 'Could not generate the historical SOW preview.',
            );
        }
    };

    const handleApply = async () => {
        if (!preview || !preview.prospectiveSow || !pricingConfigId.trim()) {
            return;
        }
        setErrorMessage(null);
        try {
            const pools =
                visiblePools.length === 0
                    ? undefined
                    : visiblePools.map((pool) => ({
                          poolType: pool.poolType,
                          initialAmount:
                              editablePools[pool.poolType] ?? formatAmount(pool.initialAmount),
                      }));
            const result = await applyCompanyHistoricalSowImport.mutateAsync({
                companyId,
                request: {
                    previewSignature: preview.previewSignature,
                    pricingConfigId,
                    name: editableName,
                    selectedSources,
                    startDate: editableStartDate,
                    endDate: editableEndDate || null,
                    allowMissingActiveLegacyContract,
                    pools,
                },
            });
            onApplied?.(result);
            onClose();
        } catch (error) {
            setErrorMessage(
                error instanceof Error
                    ? error.message
                    : 'Could not create the reconstructed SOW import.',
            );
        }
    };

    const visiblePools = preview?.prospectiveSow
        ? preview.prospectiveSow.pools.filter((pool) =>
              selectedSources.includes(pool.poolType === 'pro_credit' ? 'pro' : 'pulse'),
          )
        : [];
    const visibleImportableRows = preview
        ? preview.importableRows.filter((row) => selectedSources.includes(row.source))
        : [];
    const visibleTeamIds = [...new Set(visibleImportableRows.map((row) => row.teamId))].sort(
        (left, right) => left - right,
    );

    const handleSourceSelectionChange = (source: 'pro' | 'pulse', checked: boolean) => {
        if (!preview?.prospectiveSow) {
            return;
        }
        const nextSelectedSources = checked
            ? [...new Set([...selectedSources, source])]
            : selectedSources.filter((entry) => entry !== source);
        setSelectedSources(nextSelectedSources);
        const nextDateRange = deriveSelectedDateRange(preview.prospectiveSow, nextSelectedSources);
        setEditableStartDate(nextDateRange.startDate);
        setEditableEndDate(nextDateRange.endDate);
    };

    const applyDisabled =
        !preview?.prospectiveSow ||
        preview.conflicts.length > 0 ||
        selectedSources.length === 0 ||
        editableStartDate.trim() === '' ||
        (editableEndDate.trim() !== '' && editableEndDate < editableStartDate) ||
        (selectedSources.includes('pro') &&
            preview?.summary.legacyProCreditContracts.requiresActiveContractOverride === true &&
            !allowMissingActiveLegacyContract) ||
        visiblePools.some((pool) => {
            const value = editablePools[pool.poolType] ?? '';
            return value.trim() === '' || !Number.isFinite(Number(value));
        }) ||
        !pricingConfigId.trim() ||
        applyCompanyHistoricalSowImport.isPending;

    return (
        <DialogFrame
            open={open}
            onClose={onClose}
            title="Historical SOW import"
            description={`Preview a reconstructed SOW for ${companyName} before creating anything in the new system.`}
            panelClassName="max-w-5xl"
        >
            <div className="space-y-5">
                <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4 text-sm text-neutral-700">
                    This flow reconstructs a prospective SOW from historical credit activity. It
                    does not import into an existing active SOW.
                </div>

                {errorMessage ? (
                    <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                        {errorMessage}
                    </div>
                ) : null}

                {preview ? (
                    <div className="space-y-5">
                        <div className="grid gap-3 sm:grid-cols-4">
                            <SummaryCard
                                label="Candidates examined"
                                value={preview.summary.totalCandidatesExamined}
                            />
                            <SummaryCard
                                label="Importable"
                                value={preview.summary.importableCount}
                            />
                            <SummaryCard label="Skipped" value={preview.summary.skippedCount} />
                            <SummaryCard label="Conflicts" value={preview.summary.conflictCount} />
                        </div>

                        {preview.prospectiveSow ? (
                            <section className="rounded-lg border border-neutral-200 p-4">
                                <h3 className="text-sm font-semibold text-neutral-950">
                                    Prospective SOW
                                </h3>
                                <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-2">
                                    <div>
                                        <dt className="text-neutral-500">Name</dt>
                                        <dd className="font-medium text-neutral-950">
                                            {editableName}
                                        </dd>
                                    </div>
                                    <div>
                                        <dt className="text-neutral-500">Historical window</dt>
                                        <dd className="font-medium text-neutral-950">
                                            {formatDateTime(
                                                preview.prospectiveSow.inferredHistoricalStartDate,
                                            )}{' '}
                                            to{' '}
                                            {formatDateTime(
                                                preview.prospectiveSow.inferredHistoricalEndDate,
                                            )}
                                        </dd>
                                    </div>
                                    <div>
                                        <dt className="text-neutral-500">Teams</dt>
                                        <dd className="font-medium text-neutral-950">
                                            {visibleTeamIds.join(', ') || 'None'}
                                        </dd>
                                    </div>
                                    <div>
                                        <dt className="text-neutral-500">Pools</dt>
                                        <dd className="font-medium text-neutral-950">
                                            {visiblePools
                                                .map(
                                                    (pool) =>
                                                        `${formatPoolType(pool.poolType)} (${editablePools[pool.poolType] ?? formatAmount(pool.initialAmount)})`,
                                                )
                                                .join(', ') || 'None'}
                                        </dd>
                                    </div>
                                    <div>
                                        <dt className="text-neutral-500">
                                            Linked Pulse subscription
                                        </dt>
                                        <dd className="font-medium text-neutral-950">
                                            {preview.prospectiveSow.pulseSubscription
                                                ? `${preview.prospectiveSow.pulseSubscription.id} (${preview.prospectiveSow.pulseSubscription.scope})`
                                                : preview.summary.hasPulseSpend
                                                  ? 'None (Pulse spend present)'
                                                  : 'None (no Pulse spend)'}
                                        </dd>
                                    </div>
                                    <div>
                                        <dt className="text-neutral-500">Pulse spend</dt>
                                        <dd className="font-medium text-neutral-950">
                                            {preview.summary.hasPulseSpend
                                                ? `Yes (${formatAmount(preview.summary.pulseSpendAmount)})`
                                                : 'No'}
                                        </dd>
                                    </div>
                                    <div>
                                        <dt className="text-neutral-500">Preview totals by type</dt>
                                        <dd className="font-medium text-neutral-950">
                                            {formatPoolAmountsByType(
                                                preview.summary.candidateAmountsByPoolType,
                                            ) || 'None'}
                                        </dd>
                                    </div>
                                    <div>
                                        <dt className="text-neutral-500">
                                            Importable totals by type
                                        </dt>
                                        <dd className="font-medium text-neutral-950">
                                            {formatPoolAmountsByType(
                                                preview.summary.importableAmountsByPoolType,
                                            ) || 'None'}
                                        </dd>
                                    </div>
                                    <div>
                                        <dt className="text-neutral-500">
                                            Legacy Pro contract total
                                        </dt>
                                        <dd className="font-medium text-neutral-950">
                                            {formatAmount(
                                                preview.summary.legacyProCreditContracts
                                                    .totalCredits,
                                            )}
                                        </dd>
                                    </div>
                                    <div>
                                        <dt className="text-neutral-500">
                                            Legacy Pro contract span
                                        </dt>
                                        <dd className="font-medium text-neutral-950">
                                            {preview.summary.legacyProCreditContracts
                                                .contractStartDate
                                                ? formatDateTime(
                                                      preview.summary.legacyProCreditContracts
                                                          .contractStartDate,
                                                  )
                                                : 'Unknown'}{' '}
                                            to{' '}
                                            {preview.summary.legacyProCreditContracts
                                                .contractEndDate
                                                ? formatDateTime(
                                                      preview.summary.legacyProCreditContracts
                                                          .contractEndDate,
                                                  )
                                                : 'Open-ended'}
                                        </dd>
                                    </div>
                                    <div>
                                        <dt className="text-neutral-500">
                                            Active legacy Pro contracts
                                        </dt>
                                        <dd className="font-medium text-neutral-950">
                                            {
                                                preview.summary.legacyProCreditContracts
                                                    .activeContractCount
                                            }{' '}
                                            of{' '}
                                            {
                                                preview.summary.legacyProCreditContracts
                                                    .totalContractCount
                                            }
                                        </dd>
                                    </div>
                                </dl>
                            </section>
                        ) : null}

                        {preview.prospectiveSow ? (
                            <section className={cn(formFieldGroupSurfaceClassName, 'space-y-3')}>
                                <h3 className="text-sm font-semibold text-neutral-950">
                                    Included sources and dates
                                </h3>
                                <div>
                                    <label className="flex flex-col gap-1 text-sm text-neutral-700">
                                        <span>SOW name</span>
                                        <input
                                            type="text"
                                            aria-label="SOW name"
                                            value={editableName}
                                            onChange={(event) =>
                                                setEditableName(event.target.value)
                                            }
                                        />
                                    </label>
                                </div>
                                <div className="space-y-3">
                                    {preview.prospectiveSow.sourceOptions.map((option) => (
                                        <label
                                            key={option.source}
                                            className={cn(
                                                formFieldGroupNestedCardClassName,
                                                'flex items-start gap-3 text-sm text-neutral-800',
                                            )}
                                        >
                                            <input
                                                type="checkbox"
                                                className="mt-0.5"
                                                checked={selectedSources.includes(option.source)}
                                                onChange={(event) =>
                                                    handleSourceSelectionChange(
                                                        option.source,
                                                        event.target.checked,
                                                    )
                                                }
                                            />
                                            <span className="space-y-1">
                                                <span className="block font-medium text-neutral-950">
                                                    {`Include ${formatSourceLabel(option.source)} source`}
                                                </span>
                                                <span className="block text-neutral-600">
                                                    {formatDateTime(option.startDate)} to{' '}
                                                    {option.endDate
                                                        ? formatDateTime(option.endDate)
                                                        : 'Open-ended'}
                                                </span>
                                                <span className="block text-neutral-600">
                                                    {`${option.importableCount} importable row${option.importableCount === 1 ? '' : 's'}`}
                                                </span>
                                            </span>
                                        </label>
                                    ))}
                                </div>
                                <div className="grid gap-3 sm:grid-cols-2">
                                    <label className="flex flex-col gap-1 text-sm text-neutral-700">
                                        <span>SOW start date</span>
                                        <input
                                            type="date"
                                            aria-label="SOW start date"
                                            value={editableStartDate}
                                            onChange={(event) =>
                                                setEditableStartDate(event.target.value)
                                            }
                                        />
                                    </label>
                                    <label className="flex flex-col gap-1 text-sm text-neutral-700">
                                        <span>SOW end date</span>
                                        <input
                                            type="date"
                                            aria-label="SOW end date"
                                            value={editableEndDate}
                                            onChange={(event) =>
                                                setEditableEndDate(event.target.value)
                                            }
                                        />
                                    </label>
                                </div>
                            </section>
                        ) : null}

                        {preview.summary.legacyProCreditContracts.requiresActiveContractOverride &&
                        selectedSources.includes('pro') ? (
                            <section className="space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
                                <h3 className="text-sm font-semibold text-amber-950">
                                    Legacy contract warning
                                </h3>
                                <p className="text-sm text-amber-900">
                                    No active legacy Pro credit contract was found for this company.
                                    Review the preview carefully and explicitly override this
                                    warning before applying the historical import.
                                </p>
                                <label className="flex items-start gap-3 text-sm text-amber-950">
                                    <input
                                        type="checkbox"
                                        className="mt-0.5"
                                        checked={allowMissingActiveLegacyContract}
                                        onChange={(event) =>
                                            setAllowMissingActiveLegacyContract(
                                                event.target.checked,
                                            )
                                        }
                                    />
                                    <span>
                                        Override and allow import without an active legacy Pro
                                        contract
                                    </span>
                                </label>
                            </section>
                        ) : null}

                        {preview.conflicts.length > 0 ? (
                            <section className="space-y-3">
                                <h3 className="text-sm font-semibold text-neutral-950">
                                    Blocking conflicts
                                </h3>
                                <div className="space-y-2">
                                    {preview.conflicts.map((conflictItem) => (
                                        <div
                                            key={conflictItem.code}
                                            className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
                                        >
                                            {conflictItem.message}
                                        </div>
                                    ))}
                                </div>
                            </section>
                        ) : null}

                        {preview.warnings.length > 0 ? (
                            <section className="space-y-3">
                                <h3 className="text-sm font-semibold text-neutral-950">Warnings</h3>
                                <div className="space-y-2">
                                    {preview.warnings.map((warningItem) => (
                                        <div
                                            key={warningItem.code}
                                            className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
                                        >
                                            {warningItem.message}
                                        </div>
                                    ))}
                                </div>
                            </section>
                        ) : null}

                        {preview.prospectiveSow ? (
                            <section className={cn(formFieldGroupSurfaceClassName, 'space-y-3')}>
                                <h3 className="text-sm font-semibold text-neutral-950">
                                    Pricing configuration
                                </h3>
                                <div className="max-w-xl">
                                    <Select
                                        ariaLabel="Pricing configuration"
                                        options={creditPricingConfigs.map((config) => ({
                                            value: config.id,
                                            label: config.name,
                                        }))}
                                        value={pricingConfigId}
                                        onValueChange={setPricingConfigId}
                                        placeholder="Select pricing configuration"
                                    />
                                </div>
                                <div className="grid gap-3 sm:grid-cols-2">
                                    {visiblePools.map((pool) => (
                                        <label
                                            key={pool.poolType}
                                            className="flex flex-col gap-1 text-sm text-neutral-700"
                                        >
                                            <span>{`${formatPoolType(pool.poolType)} pool`}</span>
                                            <input
                                                type="text"
                                                inputMode="decimal"
                                                aria-label={`${formatPoolType(pool.poolType)} pool`}
                                                value={
                                                    editablePools[pool.poolType] ??
                                                    formatAmount(pool.initialAmount)
                                                }
                                                onChange={(event) =>
                                                    setEditablePools((current) => ({
                                                        ...current,
                                                        [pool.poolType]: event.target.value,
                                                    }))
                                                }
                                            />
                                        </label>
                                    ))}
                                </div>
                            </section>
                        ) : null}

                        <section className="space-y-3">
                            <h3 className="text-sm font-semibold text-neutral-950">
                                Importable rows
                            </h3>
                            {visibleImportableRows.length === 0 ? (
                                <div className="rounded-lg border border-neutral-200 p-4 text-sm text-neutral-500">
                                    No rows are importable in this preview.
                                </div>
                            ) : (
                                <div className="overflow-x-auto rounded-lg border border-neutral-200">
                                    <table className="min-w-full text-sm">
                                        <thead className="bg-neutral-50 text-left text-neutral-500">
                                            <tr>
                                                <th className="px-3 py-2 font-medium">When</th>
                                                <th className="px-3 py-2 font-medium">Source</th>
                                                <th className="px-3 py-2 font-medium">Name</th>
                                                <th className="px-3 py-2 font-medium">Team</th>
                                                <th className="px-3 py-2 font-medium">Amount</th>
                                                <th className="px-3 py-2 font-medium">Match</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {visibleImportableRows.map((row) => (
                                                <tr
                                                    key={`${row.source}-${row.sourceRowId}`}
                                                    className="border-t border-neutral-200"
                                                >
                                                    <td className="px-3 py-2 text-neutral-600">
                                                        {formatDateTime(row.occurredAt)}
                                                    </td>
                                                    <td className="px-3 py-2 uppercase text-neutral-600">
                                                        {row.source}
                                                    </td>
                                                    <td className="px-3 py-2 font-medium text-neutral-950">
                                                        {row.displayName}
                                                    </td>
                                                    <td className="px-3 py-2 text-neutral-600">
                                                        {row.teamId}
                                                    </td>
                                                    <td className="px-3 py-2 text-neutral-600">
                                                        {formatAmount(row.amount)}
                                                    </td>
                                                    <td className="px-3 py-2 text-neutral-600">
                                                        {formatMatchReason(
                                                            row.matchReason,
                                                            row.source,
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>

                        <section className="space-y-3">
                            <h3 className="text-sm font-semibold text-neutral-950">Skipped rows</h3>
                            {preview.skippedRows.length === 0 ? (
                                <div className="rounded-lg border border-neutral-200 p-4 text-sm text-neutral-500">
                                    No rows were skipped in this preview.
                                </div>
                            ) : (
                                <div className="overflow-x-auto rounded-lg border border-neutral-200">
                                    <table className="min-w-full text-sm">
                                        <thead className="bg-neutral-50 text-left text-neutral-500">
                                            <tr>
                                                <th className="px-3 py-2 font-medium">When</th>
                                                <th className="px-3 py-2 font-medium">Source</th>
                                                <th className="px-3 py-2 font-medium">Name</th>
                                                <th className="px-3 py-2 font-medium">Reason</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {preview.skippedRows.map((row) => (
                                                <tr
                                                    key={`${row.source}-${row.sourceRowId}`}
                                                    className="border-t border-neutral-200"
                                                >
                                                    <td className="px-3 py-2 text-neutral-600">
                                                        {formatDateTime(row.occurredAt)}
                                                    </td>
                                                    <td className="px-3 py-2 uppercase text-neutral-600">
                                                        {row.source}
                                                    </td>
                                                    <td className="px-3 py-2 font-medium text-neutral-950">
                                                        {row.displayName}
                                                    </td>
                                                    <td className="px-3 py-2 text-neutral-600">
                                                        {formatSkipReason(row.skipReason)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>
                    </div>
                ) : (
                    <div className="rounded-lg border border-neutral-200 p-4 text-sm text-neutral-500">
                        Generate a preview to inspect the reconstructed SOW, importable historical
                        rows, blocking conflicts, and warnings before anything is created.
                    </div>
                )}
                <Actions onCancel={onClose}>
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => void handlePreview()}
                        disabled={previewCompanyHistoricalSowImport.isPending}
                    >
                        {previewCompanyHistoricalSowImport.isPending
                            ? 'Generating...'
                            : 'Generate preview'}
                    </Button>
                    <Button
                        type="button"
                        onClick={() => void handleApply()}
                        disabled={applyDisabled}
                    >
                        Create SOW and import historical usage
                    </Button>
                </Actions>
            </div>
        </DialogFrame>
    );
}
